import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FiX, FiCreditCard } from "react-icons/fi";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
  AddressElement,
} from "@stripe/react-stripe-js";
import { useBusinessData } from "../../../api/businessApi";
import { billingApiHooks } from "../../../api/billingApi";
import {
  stripePromise,
  STRIPE_CONFIG,
  handleStripeError,
} from "../../../config/stripe";
import { debugStripe } from "../../../utils/debugHelper";

interface AddPaymentCardFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  isReplacing?: boolean;
  onCardReplacement?: (newCardId: string, oldCardId?: string) => void;
  isLoading?: boolean;
  oldCardId?: string; // ID of the card to be replaced
  businessAddress?: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    firstName?: string;
    lastName?: string;
  };
  hasExistingBillingAddress?: boolean; // Whether user has existing billing addresses
}

const AddPaymentCardForm: React.FC<AddPaymentCardFormProps> = ({
  onSuccess,
  onCancel,
  isReplacing = false,
  onCardReplacement,
  isLoading = false,
  oldCardId,
  businessAddress: propBusinessAddress,
  hasExistingBillingAddress = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [billingAddressSameAsBusiness, setBillingAddressSameAsBusiness] =
    useState(true);
  const [replacementStep, setReplacementStep] = useState<string>("");

  // Initialize the setup intents mutation hook
  const setupIntentsMutation = billingApiHooks.useSetupIntents();

  // Fetch business data using the API hook
  const {
    data: businessData,
    isLoading: isLoadingBusiness,
    error: businessError,
  } = useBusinessData();

  // Extract business address from the API response or prop
  const businessAddress = useMemo(() => {
    // Use prop business address if provided, otherwise use API data
    if (propBusinessAddress) {
      return propBusinessAddress;
    }

    if (!businessData?.data?.address) return undefined;

    return {
      line1: businessData.data.address.line_1,
      line2: businessData.data.address.line_2,
      city: businessData.data.address.city,
      state: businessData.data.address.state,
      postal_code: businessData.data.address.zip_code,
      country: businessData.data.address.country,
    };
  }, [propBusinessAddress, businessData?.data?.address]);

  // Helper function to convert country name to ISO code
  const getCountryCode = useCallback((countryName: string) => {
    const countryMap: { [key: string]: string } = {
      "United States": "US",
      USA: "US", // Support both formats
      US: "US", // Already ISO code
      Canada: "CA",
      "United Kingdom": "GB",
      Australia: "AU",
      Germany: "DE",
      France: "FR",
      Spain: "ES",
      Italy: "IT",
      Japan: "JP",
      China: "CN",
      India: "IN",
      Brazil: "BR",
      Mexico: "MX",
    };
    return countryMap[countryName] || countryName;
  }, []);

  // Helper function to convert state name to abbreviation (for US)
  const getStateCode = useCallback(
    (stateName: string, country: string) => {
      if (getCountryCode(country) === "US") {
        // If it's already a 2-letter code, return it
        if (stateName && stateName.length === 2) {
          return stateName.toUpperCase();
        }

        const stateMap: { [key: string]: string } = {
          Alabama: "AL",
          Alaska: "AK",
          Arizona: "AZ",
          Arkansas: "AR",
          California: "CA",
          Colorado: "CO",
          Connecticut: "CT",
          Delaware: "DE",
          Florida: "FL",
          Georgia: "GA",
          Hawaii: "HI",
          Idaho: "ID",
          Illinois: "IL",
          Indiana: "IN",
          Iowa: "IA",
          Kansas: "KS",
          Kentucky: "KY",
          Louisiana: "LA",
          Maine: "ME",
          Maryland: "MD",
          Massachusetts: "MA",
          Michigan: "MI",
          Minnesota: "MN",
          Mississippi: "MS",
          Missouri: "MO",
          Montana: "MT",
          Nebraska: "NE",
          Nevada: "NV",
          "New Hampshire": "NH",
          "New Jersey": "NJ",
          "New Mexico": "NM",
          "New York": "NY",
          "North Carolina": "NC",
          "North Dakota": "ND",
          Ohio: "OH",
          Oklahoma: "OK",
          Oregon: "OR",
          Pennsylvania: "PA",
          "Rhode Island": "RI",
          "South Carolina": "SC",
          "South Dakota": "SD",
          Tennessee: "TN",
          Texas: "TX",
          Utah: "UT",
          Vermont: "VT",
          Virginia: "VA",
          Washington: "WA",
          "West Virginia": "WV",
          Wisconsin: "WI",
          Wyoming: "WY",
        };
        return stateMap[stateName] || stateName;
      }
      return stateName;
    },
    [getCountryCode]
  );

  // Hooks for card replacement flow
  const setDefaultPaymentMethod = billingApiHooks.useSetDefaultPaymentMethod();
  const deletePaymentMethod = billingApiHooks.useDeletePaymentMethod();

  // State to control address default values
  const [addressDefaultValues, setAddressDefaultValues] = useState<
    | {
        name: string;
        address: {
          line1: string;
          line2: string | null;
          city: string;
          state: string;
          postal_code: string;
          country: string;
        };
      }
    | undefined
  >(undefined);

  // Handle Stripe analytics errors globally
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (
        event.reason?.message?.includes?.("r.stripe.com") ||
        event.reason?.message?.includes?.("ERR_BLOCKED_BY_CLIENT") ||
        event.reason?.message?.includes?.("Failed to fetch")
      ) {
        // Suppress Stripe analytics errors
        event.preventDefault();
        console.warn("Suppressed Stripe analytics error:", event.reason);
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  // Update address default values when business address is available
  // Always prefill the billing address from businessAddress (from Step 1)
  useEffect(() => {
    if (businessAddress && businessAddress.line1 && businessAddress.city) {
      const stateCode = getStateCode(
        businessAddress.state,
        businessAddress.country
      );
      const countryCode = getCountryCode(businessAddress.country);

      console.log("📍 Converting address for Stripe:");
      console.log("  Original state:", businessAddress.state);
      console.log("  Converted state:", stateCode);
      console.log("  Original country:", businessAddress.country);
      console.log("  Converted country:", countryCode);

      const newDefaults = {
        name:
          `${propBusinessAddress?.firstName || ""} ${
            propBusinessAddress?.lastName || ""
          }`.trim() || "Business Address",
        address: {
          line1: businessAddress.line1,
          line2: businessAddress.line2,
          city: businessAddress.city,
          state: stateCode,
          postal_code: businessAddress.postal_code,
          country: countryCode,
        },
      };
      console.log("✅ Final address defaults for Stripe:", newDefaults);
      console.log("Business address from prop:", propBusinessAddress);
      console.log("Business address from API:", businessAddress);
      setAddressDefaultValues(newDefaults);
    } else {
      console.log("⚠️ Clearing address defaults - no valid business address");
      console.log("businessAddress:", businessAddress);
      setAddressDefaultValues(undefined);
    }
  }, [
    businessAddress?.line1,
    businessAddress?.line2,
    businessAddress?.city,
    businessAddress?.state,
    businessAddress?.postal_code,
    businessAddress?.country,
    propBusinessAddress?.firstName,
    propBusinessAddress?.lastName,
    getStateCode,
    getCountryCode,
  ]);

  // Debug logging for address default values
  useEffect(() => {
    console.log("AddressElement defaultValues changed:", addressDefaultValues);
  }, [addressDefaultValues]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation(); // Additional safeguard to prevent event bubbling

    if (!stripe || !elements || isLoading) {
      if (!stripe) {
        setErrorMessage(
          "Payment system is not available. Please refresh the page and try again."
        );
      }
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setReplacementStep("");

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message || "An error occurred");
      setLoading(false);
      return;
    }

    try {
      // Prepare request body with billing address
      const requestBody: any = {};

      if (billingAddressSameAsBusiness && businessAddress) {
        // Use business address when checkbox is checked
        requestBody.billing_address = {
          line1: businessAddress.line1,
          line2: businessAddress.line2,
          city: businessAddress.city,
          state: businessAddress.state,
          postal_code: businessAddress.postal_code,
          country: businessAddress.country,
        };
      } else if (businessAddress) {
        // Collect billing address from AddressElement when checkbox is not checked
        const addressElement = elements.getElement(AddressElement);
        if (addressElement) {
          const { complete, value } = await addressElement.getValue();
          if (complete && value.address) {
            requestBody.billing_address = {
              line1: value.address.line1 || "",
              line2: value.address.line2 || "",
              city: value.address.city || "",
              state: value.address.state || "",
              postal_code: value.address.postal_code || "",
              country: value.address.country || "",
            };
          }
        }
      } else {
        // No business address available or API error, collect from AddressElement
        const addressElement = elements.getElement(AddressElement);
        if (addressElement) {
          const { complete, value } = await addressElement.getValue();
          if (complete && value.address) {
            requestBody.billing_address = {
              line1: value.address.line1 || "",
              line2: value.address.line2 || "",
              city: value.address.city || "",
              state: value.address.state || "",
              postal_code: value.address.postal_code || "",
              country: value.address.country || "",
            };
          }
        }
      }

      const res = await setupIntentsMutation.mutateAsync(requestBody);

      if (!res || !res.client_secret) {
        throw new Error("Failed to create setup intent");
      }

      const { client_secret: clientSecret } = res;

      debugStripe.logPaymentStep("Starting stripe.confirmSetup", {
        clientSecret: clientSecret?.substring(0, 20) + "...",
      });

      let result: any;
      try {
        result = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            return_url: window.location.origin + window.location.pathname,
          },
          redirect: "if_required", // Only redirect if necessary (e.g., 3D Secure)
        });
        debugStripe.logStripeOperation("confirmSetup success", { result });
      } catch (error) {
        debugStripe.logError("stripe.confirmSetup", error);

        // Only catch network/analytics errors, not payment errors
        if (handleStripeError(error)) {
          console.warn(
            "Stripe analytics error caught, but payment may have succeeded:",
            error
          );
          // For analytics errors, we need to check if the payment actually succeeded
          // We can't assume success, so we'll show an appropriate message
          setErrorMessage(
            "Payment processing completed, but we couldn't verify the result. Please check your payment method or contact support."
          );
          setLoading(false);
          return;
        }
        // Re-throw legitimate errors
        throw error;
      }

      // Stripe confirmSetup response structure:
      // - On success: { setupIntent: { payment_method: string, ... } }
      // - On error: { error: { message: string, ... } }

      // Check if the result has an error
      if (result.error) {
        setErrorMessage(result.error.message || "Payment failed");
        setLoading(false);
        return;
      }

      // Check if we have a valid setup intent
      if (!result.setupIntent) {
        setErrorMessage("Payment verification failed. Please try again.");
        setLoading(false);
        return;
      }

      // Type guard to ensure we have a valid setup intent with payment method
      const setupIntent = result.setupIntent;
      if (!setupIntent.payment_method) {
        setErrorMessage("Payment method not found. Please try again.");
        setLoading(false);
        return;
      }

      // If we're replacing a card, implement the replacement flow
      if (isReplacing) {
        const newCardId = setupIntent.payment_method as string;

        try {
          // Step 1: Set the new card as default
          setReplacementStep("Setting new card as default...");
          await setDefaultPaymentMethod.mutateAsync(newCardId);

          // Step 2: Delete the old card if we have its ID
          if (oldCardId) {
            setReplacementStep("Removing old card...");
            await deletePaymentMethod.mutateAsync(oldCardId);
          }

          // Step 3: Call the replacement callback with the new card ID
          if (onCardReplacement) {
            onCardReplacement(newCardId, oldCardId);
          }

          // Step 4: Close the dialog and update UI
          onSuccess();
        } catch (error) {
          console.error("Card replacement error:", error);
          setErrorMessage(
            "Card was added but replacement failed. Please try again."
          );
          // Still call onSuccess to close the dialog since the new card was added
          onSuccess();
        }
      } else {
        // Not replacing, just add the card
        onSuccess();
      }
    } catch (error) {
      setErrorMessage("An error occurred while processing payment");
      console.error("Payment error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debug logging for billing address checkbox
  console.log(
    "🔍 DEBUG - AddPaymentCardForm: hasExistingBillingAddress:",
    hasExistingBillingAddress
  );
  console.log(
    "🔍 DEBUG - AddPaymentCardForm: businessAddress:",
    businessAddress
  );
  console.log(
    "🔍 DEBUG - AddPaymentCardForm: Should show checkbox:",
    hasExistingBillingAddress && businessAddress
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-2 min-h-0">
      {businessAddress && (
        <div className="flex items-center gap-1.5 p-1.5 bg-gray-50 rounded-lg border border-gray-200">
          <input
            type="checkbox"
            id="billingAddressSame"
            checked={billingAddressSameAsBusiness}
            onChange={(e) => {
              setBillingAddressSameAsBusiness(e.target.checked);
              if (e.target.checked && businessAddress) {
                setAddressDefaultValues({
                  name:
                    `${propBusinessAddress?.firstName || ""} ${
                      propBusinessAddress?.lastName || ""
                    }`.trim() || "Business Address",
                  address: {
                    line1: businessAddress.line1,
                    line2: businessAddress.line2,
                    city: businessAddress.city,
                    state: getStateCode(
                      businessAddress.state,
                      businessAddress.country
                    ),
                    postal_code: businessAddress.postal_code,
                    country: getCountryCode(businessAddress.country),
                  },
                });
              } else {
                setAddressDefaultValues(undefined);
              }
            }}
            className="w-3 h-3 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
          />
          <label htmlFor="billingAddressSame" className="text-xs text-gray-700">
            Billing address same as business address
          </label>
        </div>
      )}

      {/* Show message when no business address is available for checkbox */}
      {!isLoadingBusiness && !businessAddress && !businessError && (
        <div className="flex items-center gap-1.5 p-1.5 bg-yellow-50 rounded-lg border border-yellow-200">
          <span className="text-xs text-yellow-800">
            No business address available. Billing address will be collected
            manually.
          </span>
        </div>
      )}

      {/* Show error message for checkbox when business API call fails */}
      {businessError && (
        <div className="flex items-center gap-1.5 p-1.5 bg-red-50 rounded-lg border border-red-200">
          <span className="text-xs text-red-800">
            Failed to load business address. Billing address will be collected
            manually.
          </span>
        </div>
      )}

      {/* Browser compatibility notice for Stripe */}
      <div className="flex items-center gap-1.5 p-1.5 bg-blue-50 rounded-lg border border-blue-200">
        <span className="text-xs text-blue-800">
          💡 <strong>Tip:</strong> If you experience payment issues, try
          disabling ad blockers or privacy extensions for this site.
        </span>
      </div>

      {/* Show loading state for checkbox when business data is being fetched */}
      {isLoadingBusiness && (
        <div className="flex items-center gap-1.5 p-1.5 bg-gray-50 rounded-lg border border-gray-200">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
          <span className="text-xs text-gray-500">
            Loading business address...
          </span>
        </div>
      )}

      {/* Show business address preview when checkbox is checked */}
      {billingAddressSameAsBusiness && businessAddress && (
        <div className="p-1.5 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-800 font-medium mb-1">
            Billing Address Preview:
          </p>
          <div className="text-xs text-blue-700 space-y-0.5">
            <p>{businessAddress.line1}</p>
            {businessAddress.line2 && <p>{businessAddress.line2}</p>}
            <p>
              {businessAddress.city}, {businessAddress.state}{" "}
              {businessAddress.postal_code}
            </p>
            <p>{businessAddress.country}</p>
          </div>
        </div>
      )}

      {/* Show loading state when business data is being fetched */}
      {isLoadingBusiness && (
        <div className="p-1.5 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
            <span className="text-xs">Loading business address...</span>
          </div>
        </div>
      )}

      {/* Show message when no business address is available */}
      {!isLoadingBusiness && !businessAddress && !businessError && (
        <div className="p-1.5 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-xs text-yellow-800">
            No business address found. Please add a business address in your
            business settings first.
          </p>
        </div>
      )}

      {/* Show error message when business API call fails */}
      {businessError && (
        <div className="p-1.5 bg-red-50 rounded-lg border border-red-200">
          <p className="text-xs text-red-800">
            Failed to load business address. Please try again or add billing
            address manually.
          </p>
        </div>
      )}

      {/* Show replacement progress */}
      {isReplacing && replacementStep && (
        <div className="p-1.5 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 text-blue-800">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-xs font-medium">{replacementStep}</span>
          </div>
        </div>
      )}

      {/* Payment Element and Address Element in side-by-side layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Left side - Payment Element for card details */}
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">
            Card Details
          </label>
          <div className="min-h-[140px] border border-gray-200 rounded-lg p-1">
            <PaymentElement />
          </div>
        </div>

        {/* Right side - Address Element for billing address */}
        {businessAddress ? (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-gray-700">
                Billing Address
              </label>
              <span
                className={`text-xs px-1 py-0.5 rounded-full ${
                  billingAddressSameAsBusiness
                    ? "bg-green-100 text-green-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {billingAddressSameAsBusiness ? "Prefilled" : "Manual Input"}
              </span>
            </div>
            <div className="min-h-[140px] border border-gray-200 rounded-lg p-1">
              {addressDefaultValues ? (
                <AddressElement
                  key={`address-${Date.now()}-${JSON.stringify(
                    addressDefaultValues
                  )}`}
                  options={{
                    mode: "billing",
                    defaultValues: addressDefaultValues,
                    fields: {
                      phone: "never",
                    },
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-[140px]">
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                    <span className="text-xs">Loading address...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : isLoadingBusiness ? (
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Billing Address
            </label>
            <div className="min-h-[140px] border border-gray-200 rounded-lg p-1 flex items-center justify-center">
              <div className="flex items-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                <span className="text-xs">Loading...</span>
              </div>
            </div>
          </div>
        ) : businessError ? (
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Billing Address
            </label>
            <div className="min-h-[140px] border border-gray-200 rounded-lg p-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <p className="text-xs">Failed to load business address</p>
                <p className="text-xs mt-1">
                  Please enter billing address manually
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Billing Address
            </label>
            <div className="min-h-[140px] border border-gray-200 rounded-lg p-1">
              <AddressElement
                options={{
                  mode: "billing",
                  fields: {
                    phone: "never",
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="text-red-500 text-xs bg-red-50 p-1.5 rounded-lg">
          {errorMessage}
        </div>
      )}

      {/* Action Buttons - Fixed at bottom */}
      <div className="flex gap-2 pt-2 border-t border-gray-200 mt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed text-xs font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading || isLoading || isLoadingBusiness}
          className="flex-1 bg-[#0DB17B] text-white px-2.5 py-1.5 rounded-lg hover:bg-[#0DB17B]/80 disabled:bg-gray-300 disabled:cursor-not-allowed text-xs font-medium"
        >
          {isLoading
            ? "Processing Replacement..."
            : loading
            ? replacementStep || "Processing..."
            : isLoadingBusiness
            ? "Loading..."
            : isReplacing
            ? "Replace Card"
            : "Add Card"}
        </button>
      </div>
    </form>
  );
};

interface AddPaymentCardDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isReplacing?: boolean;
  onCardReplacement?: (newCardId: string, oldCardId?: string) => void;
  isLoading?: boolean;
  oldCardId?: string; // ID of the card to be replaced
  businessAddress?: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    firstName?: string;
    lastName?: string;
  }; // Business address from UserStateStepper form
  hasExistingBillingAddress?: boolean; // Whether user has existing billing addresses
}

const AddPaymentCardDialog: React.FC<AddPaymentCardDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  isReplacing = false,
  onCardReplacement,
  isLoading = false,
  oldCardId,
  businessAddress: propBusinessAddress,
  hasExistingBillingAddress = false,
}) => {
  const options = STRIPE_CONFIG.options;

  // Debug logging for dialog props
  console.log(
    "🔍 DEBUG - AddPaymentCardDialog: hasExistingBillingAddress:",
    hasExistingBillingAddress
  );
  console.log(
    "🔍 DEBUG - AddPaymentCardDialog: propBusinessAddress:",
    propBusinessAddress
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] shadow-lg flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FiCreditCard className="text-blue-600" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold">
                {isReplacing ? "Replace Payment Method" : "Add Payment Method"}
              </h3>
              <p className="text-sm text-gray-600">
                {isLoading
                  ? "Processing card replacement... Please wait."
                  : isReplacing
                  ? "Your new card will be set as default and old cards will be removed"
                  : "Enter your card details"}
              </p>
              {isLoading && (
                <div className="mt-2 flex items-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-xs">Setting up replacement...</span>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed"
            aria-label="Close dialog"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <Elements stripe={stripePromise} options={options}>
            <AddPaymentCardForm
              onSuccess={onSuccess}
              onCancel={onClose}
              isReplacing={isReplacing}
              onCardReplacement={onCardReplacement}
              isLoading={isLoading}
              oldCardId={oldCardId}
              businessAddress={propBusinessAddress}
              hasExistingBillingAddress={hasExistingBillingAddress}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentCardDialog;
